/**
 * Hash-based routing.
 *
 * Hash rather than History API because the same bundle ships three ways: as a
 * web page, as a PWA, and inside Capacitor where the document is served from
 * `capacitor://localhost` or `file://`. Only the first of those has a server
 * that can rewrite deep links to index.html; the other two would 404 on a
 * refresh. A hash never leaves the document.
 */

import { NavigationHistory } from './navigationHistory';

export const ROUTES = [
  'menu',
  'play',
  'solo',
  'versus',
  'rankings',
  'history',
  'profile',
  'achievements',
  'highscores',
  'settings',
  'login',
  'register',
] as const;

export type RouteName = (typeof ROUTES)[number];

export interface Route {
  name: RouteName;
  params: Readonly<Record<string, string>>;
}

/**
 * Why the player is leaving, which is the part a guard needs in order to send
 * them back where they were going once it is done asking.
 *
 * `external` is the browser's own back button, Android's hardware key or a hand
 * edited URL: navigations the app never initiated and therefore cannot stop
 * before they happen, only undo.
 */
export type NavigationIntent =
  { kind: 'go'; route: Route } | { kind: 'back' } | { kind: 'external'; route: Route };

/** Return false to stop the navigation and take responsibility for resuming it. */
export type NavigationGuard = (intent: NavigationIntent) => boolean;

const DEFAULT: Route = { name: 'menu', params: {} };

function parse(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  if (!raw) return DEFAULT;

  const [path = '', query = ''] = raw.split('?');
  const name = path as RouteName;
  if (!ROUTES.includes(name)) return DEFAULT;

  return { name, params: Object.fromEntries(new URLSearchParams(query)) };
}

function serialise(route: Route): string {
  const query = new URLSearchParams(route.params).toString();
  return `#/${route.name}${query ? `?${query}` : ''}`;
}

class Router {
  current = $state<Route>(DEFAULT);

  /**
   * Historial de navegación, no una sola casilla.
   *
   * Antes esto era un único `previous`, y eso convertía `back()` en un bucle:
   * volver es en sí una navegación, así que al llegar a la pantalla anterior
   * `previous` pasaba a ser la que acababas de abandonar. Con menú →
   * configuración → perfil, el botón rebotaba entre configuración y perfil sin
   * alcanzar nunca el inicio.
   */
  private readonly history = new NavigationHistory();

  /** Distingue una vuelta atrás de una navegación nueva en `hashchange`. */
  private goingBack = false;

  /**
   * Protección de salida para una pantalla con cambios sin guardar.
   *
   * Vive en el router y no en la vista porque la vista sólo controla sus
   * propios botones: el atrás del navegador, el del hardware de Android y una
   * URL escrita a mano no pasan por ella. Sólo hay una ranura porque sólo hay
   * una vista montada a la vez, y dos formularios pidiendo confirmación sobre
   * la misma navegación no tendrían un orden defendible.
   */
  private guard?: NavigationGuard;

  /** La navegación que el guard frenó, a la espera de repetirse o descartarse. */
  private pending: NavigationIntent | null = null;

  /** Encendida mientras se repite una navegación ya autorizada. */
  private bypassing = false;

  /** El router está escribiendo el hash: el guard ya opinó sobre este cambio. */
  private selfDriven = false;

  /** Estamos deshaciendo un cambio de hash que el guard rechazó. */
  private rewinding = false;

  start(): () => void {
    this.current = parse(window.location.hash);

    const onHashChange = (): void => {
      const target = parse(window.location.hash);

      // Nuestra propia marcha atrás sobre una navegación rechazada. No es una
      // navegación: no se apila, no cambia `current` y no vuelve a preguntar.
      if (this.rewinding) {
        this.rewinding = false;
        return;
      }

      if (this.selfDriven) {
        this.selfDriven = false;
      } else if (!this.requestLeave({ kind: 'external', route: target })) {
        // El hash ya cambió antes de que nadie pudiera opinar — es lo único que
        // el navegador ofrece — así que cancelarlo es devolverlo a su sitio.
        this.rewinding = true;
        window.location.hash = serialise(this.current);
        return;
      }

      // Sólo se apila lo que se deja atrás avanzando. Al volver, la entrada ya
      // se ha sacado de la pila: apilarla de nuevo es exactamente lo que
      // producía el bucle.
      if (this.goingBack) this.goingBack = false;
      else this.history.push(this.current);

      this.current = target;
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }

  go(name: RouteName, params: Record<string, string> = {}): void {
    const next = { name, params };
    const target = serialise(next);

    // Ir a donde ya se está no es una navegación, y preguntarle al guard por
    // ella abriría un diálogo de salida sin salida.
    if (target === window.location.hash) return;
    if (!this.requestLeave({ kind: 'go', route: next })) return;

    // Assigning the hash fires `hashchange`, which is what updates `current` —
    // so navigation behaves identically whether it came from a button or from
    // the player editing the URL.
    this.assign(target);
  }

  back(): void {
    if (!this.requestLeave({ kind: 'back' })) return;

    // La pila se toca sólo después de que la salida esté autorizada: sacar una
    // entrada de una navegación que no llega a ocurrir la perdería.
    const previous = this.history.pop() ?? DEFAULT;
    const target = serialise(previous);

    // Si el destino ya es el hash actual no habrá `hashchange`, y la bandera se
    // quedaría encendida envenenando la siguiente navegación.
    if (target === window.location.hash) {
      this.current = previous;
      return;
    }

    this.goingBack = true;
    this.assign(target);
  }

  /**
   * Registra la protección de salida de la vista montada. Devuelve la baja, que
   * la vista debe llamar al desmontarse: un guard huérfano bloquearía la
   * aplicación entera desde una pantalla que ya no existe.
   */
  guardNavigation(guard: NavigationGuard): () => void {
    this.guard = guard;
    return () => {
      if (this.guard !== guard) return;
      this.guard = undefined;
      this.pending = null;
    };
  }

  /** Repite la navegación que el guard frenó, sin volver a preguntar. */
  resumePending(): void {
    const intent = this.pending;
    this.pending = null;
    if (!intent) return;

    this.bypassing = true;
    try {
      if (intent.kind === 'back') this.back();
      else this.go(intent.route.name, { ...intent.route.params });
    } finally {
      this.bypassing = false;
    }
  }

  /** El jugador decidió quedarse. Olvida el destino. */
  cancelPending(): void {
    this.pending = null;
  }

  /**
   * Pregunta al guard y recuerda el destino si dice que no. Guardar el destino
   * es lo que permite que «guardar y salir» termine el viaje que el jugador
   * había empezado, en vez de dejarlo en la pantalla que quería abandonar.
   */
  private requestLeave(intent: NavigationIntent): boolean {
    if (this.bypassing || !this.guard) return true;
    if (this.guard(intent)) {
      this.pending = null;
      return true;
    }

    this.pending = intent;
    return false;
  }

  private assign(hash: string): void {
    this.selfDriven = true;
    window.location.hash = hash;
  }
}

export const router = new Router();
